import React, { useState, useEffect, useCallback } from 'react';
import Navbar from '../components/Navbar';
import './Settings.css';

const Settings = () => {
    const [user, setUser] = useState(null);
    const [activeTab, setActiveTab] = useState('perfil');
    const [loading, setLoading] = useState(true);

    // Perfil
    const [profileData, setProfileData] = useState({
        username: '',
        email: '',
        full_name: '',
        photo: ''
    });

    // Senha
    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
        mfaToken: ''
    });

    // MFA Setup
    const [mfaSetup, setMfaSetup] = useState({
        showQR: false,
        secret: '',
        otpauth: '',
        token: '',
        method: 'email',
        isConfirmingDisable: false
    });

    // Admin
    const [adminUsers, setAdminUsers] = useState([]);
    const [showAdminModal, setShowAdminModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [newAdminUser, setNewAdminUser] = useState({
        username: '',
        password: '',
        email: '',
        full_name: '',
        isAdmin: false,
        allowedTabs: ['clientes', 'produtos', 'funcionarios', 'financeiro', 'fornecedores', 'calendario', 'cotacoes'],
        allowedWidgets: ['weather', 'calendar', 'finance', 'notes', 'quotations']
    });

    // Logs
    const [systemLogs, setSystemLogs] = useState([]);
    const [selectedLog, setSelectedLog] = useState(null);
    const [showLogModal, setShowLogModal] = useState(false);
    const [logFilters, setLogFilters] = useState({
        username: '',
        tableName: '',
        recordId: '',
        startDate: '',
        endDate: ''
    });
    const [logCleanupRetention, setLogCleanupRetention] = useState('0'); // 0 = Não limpar, 3, 6, 12

    // Redundancy Config
    const [redundancyConfig, setRedundancyConfig] = useState({
        peer_url: '',
        sync_secret: '',
        sync_enabled: '0',
        node_mode: 'host',
        sync_frequency: '1m',
        sync_schedule_time: '00:00',
        sync_schedule_day: '0',
        maintenance_mode: '0'
    });

    const [syncStatus, setSyncStatus] = useState({
        enabled: false,
        isConfigured: false,
        hasInconsistency: false,
        pendingCount: 0,
        maintenanceMode: false
    });

    // Email
    const [emailData, setEmailData] = useState({
        to: '',
        subject: '',
        text: ''
    });
    const [sendingEmail, setSendingEmail] = useState(false);

    // Notificações
    const [notificationSettings, setNotificationSettings] = useState([]);
    const [savingNotifications, setSavingNotifications] = useState(false);

    // Bloqueios de IP
    const [blockedIPs, setBlockedIPs] = useState([]);
    const [loadingBlocks, setLoadingBlocks] = useState(false);
    const [newBlock, setNewBlock] = useState({ ip_address: '', reason: '', expires_at: '' });

    // IPs Confiáveis (Whitelist)
    const [trustedIPs, setTrustedIPs] = useState([]);
    const [loadingTrusted, setLoadingTrusted] = useState(false);
    const [showTrustedModal, setShowTrustedModal] = useState(false);
    const [isEditingTrusted, setIsEditingTrusted] = useState(false);
    const [trustedFormData, setTrustedFormData] = useState({ id: '', ip_address: '', description: '' });

    const fetchAdminUsers = useCallback(async () => {
        const res = await fetch('/api/admin/users', { credentials: 'include' });
        if (res.ok) {
            const data = await res.json();
            setAdminUsers(data);
        }
    }, []);

    const fetchLogs = useCallback(async () => {
        const queryParams = new URLSearchParams();
        Object.entries(logFilters).forEach(([key, value]) => {
            if (value) queryParams.append(key, value);
        });

        const res = await fetch(`/api/admin/logs?${queryParams.toString()}`, { credentials: 'include' });
        if (res.ok) {
            const data = await res.json();
            setSystemLogs(data);
        }
    }, [logFilters]);

    const fetchRedundancyConfig = useCallback(async () => {
        const res = await fetch('/api/admin/redundancy', { credentials: 'include' });
        if (res.ok) {
            const data = await res.json();
            setRedundancyConfig(data);
        }
    }, []);

    const fetchSyncStatus = useCallback(async () => {
        const res = await fetch('/api/sync/status', { credentials: 'include' });
        if (res.ok) {
            const data = await res.json();
            setSyncStatus(data);
            // Atualizar o estado local da manutenção se vier do servidor
            setRedundancyConfig(prev => ({ ...prev, maintenance_mode: data.maintenanceMode ? '1' : '0' }));
        }
    }, []);

    const fetchNotificationSettings = useCallback(async () => {
        const res = await fetch('/api/admin/notifications', { credentials: 'include' });
        if (res.ok) {
            const data = await res.json();
            setNotificationSettings(data);
        }
    }, []);

    const fetchBlockedIPs = useCallback(async () => {
        setLoadingBlocks(true);
        try {
            const res = await fetch('/api/admin/ip-blocks', { credentials: 'include' });
            if (res.ok) {
                const data = await res.json();
                setBlockedIPs(data);
            }
        } catch (error) {
            console.error('Erro ao buscar IPs bloqueados:', error);
        } finally {
            setLoadingBlocks(false);
        }
    }, []);

    const fetchTrustedIPs = useCallback(async () => {
        setLoadingTrusted(true);
        try {
            const res = await fetch('/api/admin/trusted-ips', { credentials: 'include' });
            if (res.ok) {
                const data = await res.json();
                setTrustedIPs(data);
            }
        } catch (error) {
            console.error('Erro ao buscar IPs confiáveis:', error);
        } finally {
            setLoadingTrusted(false);
        }
    }, []);

    const fetchUserInfo = useCallback(async () => {
        const res = await fetch('/api/auth/me', { credentials: 'include' });
        if (res.ok) {
            const data = await res.json();
            setUser(data.user);
            setProfileData({
                username: data.user.username,
                email: data.user.email,
                full_name: data.user.full_name || '',
                photo: data.user.photo || ''
            });
            if (data.user.isAdmin) {
                fetchAdminUsers();
                fetchLogs();
                fetchRedundancyConfig();
                fetchSyncStatus();
            }
        }
        setLoading(false);
    }, [fetchAdminUsers, fetchLogs, fetchRedundancyConfig]);

    useEffect(() => {
        fetchUserInfo();
    }, [fetchUserInfo]);

    useEffect(() => {
        if (activeTab === 'email' && user?.isAdmin) {
            fetchNotificationSettings();
        }
        if (activeTab === 'bloqueios' && user?.isAdmin) {
            fetchBlockedIPs();
            fetchTrustedIPs();
        }
    }, [activeTab, user, fetchNotificationSettings, fetchBlockedIPs, fetchTrustedIPs]);

    const handleUnblockIP = async (id) => {
        if (!window.confirm('Deseja realmente desbloquear este IP?')) return;
        try {
            const res = await fetch('/api/admin/unblock-ip', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id }),
                credentials: 'include'
            });
            if (res.ok) {
                alert('IP desbloqueado com sucesso!');
                fetchBlockedIPs();
            } else {
                alert('Erro ao desbloquear IP.');
            }
        } catch (error) {
            console.error('Erro ao conectar para desbloqueio:', error);
        }
    };

    const handleManualBlock = async (e) => {
        e.preventDefault();
        try {
            const res = await fetch('/api/admin/block-ip', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newBlock),
                credentials: 'include'
            });
            if (res.ok) {
                alert('IP bloqueado com sucesso!');
                setNewBlock({ ip_address: '', reason: '', expires_at: '' });
                fetchBlockedIPs();
            } else {
                alert('Erro ao bloquear IP.');
            }
        } catch (error) {
            console.error('Erro ao bloquear IP:', error);
        }
    };

    const handleTrustedSubmit = async (e) => {
        e.preventDefault();
        const method = isEditingTrusted ? 'PUT' : 'POST';
        try {
            const res = await fetch('/api/admin/trusted-ips', {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(trustedFormData),
                credentials: 'include'
            });
            if (res.ok) {
                alert(isEditingTrusted ? 'IP confiável atualizado!' : 'IP confiável adicionado!');
                setShowTrustedModal(false);
                fetchTrustedIPs();
            } else {
                alert('Erro ao processar IP confiável.');
            }
        } catch (error) {
            console.error('Erro:', error);
        }
    };

    const handleDeleteTrusted = async (id) => {
        if (!window.confirm('Deseja remover este IP da lista de confiança?')) return;
        try {
            const res = await fetch('/api/admin/trusted-ips/delete', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id }),
                credentials: 'include'
            });
            if (res.ok) {
                alert('IP removido com sucesso!');
                fetchTrustedIPs();
            } else {
                alert('Erro ao remover IP.');
            }
        } catch (error) {
            console.error('Erro:', error);
        }
    };

    const handleToggleNotification = (userId, alertType, checked) => {
        setNotificationSettings(prev => prev.map(u => {
            if (u.id === userId) {
                const newAlerts = checked
                    ? [...u.alerts, alertType]
                    : u.alerts.filter(a => a !== alertType);
                return { ...u, alerts: newAlerts };
            }
            return u;
        }));
    };

    const handleSaveNotifications = async () => {
        setSavingNotifications(true);
        try {
            const settings = notificationSettings.map(u => ({
                userId: u.id,
                alerts: u.alerts
            }));

            const res = await fetch('/api/admin/notifications', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ settings }),
                credentials: 'include'
            });

            if (res.ok) {
                alert('Configurações de notificação salvas!');
            } else {
                alert('Erro ao salvar configurações.');
            }
        } catch (error) {
            console.error(error);
            alert('Erro de conexão ao salvar.');
        } finally {
            setSavingNotifications(false);
        }
    };

    const handlePhotoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('photo', file);

        const res = await fetch('/api/settings/upload-photo', {
            method: 'POST',
            body: formData,
            credentials: 'include'
        });

        const data = await res.json();
        if (res.ok) {
            alert('Foto atualizada!');
            setProfileData({ ...profileData, photo: data.photo });
            fetchUserInfo();
        }
    };

    const handleProfileSubmit = async (e) => {
        e.preventDefault();
        const res = await fetch('/api/settings/profile', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(profileData),
            credentials: 'include'
        });
        if (res.ok) alert('Perfil atualizado!');
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        if (passwordData.newPassword !== passwordData.confirmPassword) return alert('Senhas não conferem!');

        const res = await fetch('/api/settings/password', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                currentPassword: passwordData.currentPassword,
                newPassword: passwordData.newPassword,
                mfaToken: passwordData.mfaToken
            }),
            credentials: 'include'
        });
        const data = await res.json();
        if (res.ok) {
            alert('Senha alterada!');
            setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '', mfaToken: '' });
        } else {
            alert(data.message || 'Erro ao alterar senha.');
        }
    };

    const handleMFASetup = async (action, method, token) => {
        if (action === 'generate_qr') {
            const res = await fetch('/api/settings/mfa', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'generate_qr', method: 'app' }),
                credentials: 'include'
            });
            const data = await res.json();
            setMfaSetup({ ...mfaSetup, showQR: true, secret: data.secret, otpauth: data.otpauth_url });
            return;
        }

        if (action === 'disable') {
            const res = await fetch('/api/settings/mfa', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'request_disable' }),
                credentials: 'include'
            });
            if (res.ok) {
                alert('Um código de confirmação foi enviado para seu e-mail.');
                setMfaSetup({ ...mfaSetup, isConfirmingDisable: true, token: '' });
                return;
            } else {
                const data = await res.json();
                alert(data.message || 'Erro ao solicitar desativação.');
                return;
            }
        }

        const finalAction = action === 'confirm_disable' ? 'confirm_disable' : action;

        const res = await fetch('/api/settings/mfa', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: finalAction, method, token }),
            credentials: 'include'
        });
        if (res.ok) {
            alert(action === 'enable' ? '2FA Ativado!' : '2FA Desativado!');
            fetchUserInfo();
            setMfaSetup({ ...mfaSetup, showQR: false, token: '', isConfirmingDisable: false });
        } else {
            const data = await res.json();
            alert(data.message || 'Erro ao processar solicitação.');
        }
    };

    const handleAdminCreate = async (e) => {
        e.preventDefault();
        const res = await fetch('/api/admin/users', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                ...newAdminUser,
                allowed_tabs: newAdminUser.allowedTabs,
                allowed_widgets: newAdminUser.allowedWidgets
            }),
            credentials: 'include'
        });
        if (res.ok) {
            alert('Usuário criado!');
            setShowAdminModal(false);
            fetchAdminUsers();
        }
    };

    const handleAdminReset = async (userId) => {
        if (!window.confirm('Resetar senha deste usuário? Uma nova senha será enviada por email.')) return;
        const res = await fetch('/api/admin/users/reset-password', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userId }),
            credentials: 'include'
        });
        if (res.ok) alert('Senha resetada com sucesso!');
    };

    const handleAdminRemove2FA = async (userId) => {
        if (!window.confirm('Remover 2FA deste usuário?')) return;
        const res = await fetch('/api/admin/users/remove-mfa', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userId }),
            credentials: 'include'
        });
        const data = await res.json();
        if (res.ok) {
            alert('2FA removido!');
            fetchAdminUsers();
        } else {
            alert(data.message || 'Erro ao remover 2FA.');
        }
    };

    const handleEditUser = (user) => {
        setEditingUser({
            ...user,
            newIsAdmin: user.isAdmin === 1,
            allowedTabs: user.allowed_tabs ? JSON.parse(user.allowed_tabs) : [],
            allowedWidgets: user.allowed_widgets ? JSON.parse(user.allowed_widgets) : ['weather', 'calendar', 'finance', 'notes', 'quotations']
        });
        setShowEditModal(true);
    };

    const handleAdminUpdateUser = async (e) => {
        e.preventDefault();
        const res = await fetch(`/api/admin/users/${editingUser.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                userId: editingUser.id,
                email: editingUser.email,
                isAdmin: editingUser.newIsAdmin,
                allowed_tabs: editingUser.allowedTabs,
                allowed_widgets: editingUser.allowedWidgets
            }),
            credentials: 'include'
        });
        const data = await res.json();
        if (res.ok) {
            alert('Usuário atualizado!');
            setShowEditModal(false);
            fetchAdminUsers();
        } else {
            alert(data.message || 'Erro ao atualizar usuário.');
        }
    };

    const handleViewLogDetails = (log) => {
        let details = log.details;
        try {
            if (typeof details === 'string') details = JSON.parse(details);
        } catch (e) {
            console.error('Erro ao parsear detalhes do log');
        }
        setSelectedLog({ ...log, parsedDetails: details });
        setShowLogModal(true);
    };

    const renderLogDetails = (details) => {
        if (!details || typeof details !== 'object') return String(details);

        const mainKeys = Object.keys(details).filter(k => !k.startsWith('_old_'));

        const renderValue = (val, key) => {
            if (Array.isArray(val)) {
                return (
                    <div className="log-array-container">
                        {val.map((item, idx) => (
                            <div key={idx} className="log-array-item">
                                {typeof item === 'object' ? (
                                    Object.entries(item).map(([k, v]) => (
                                        <div key={k} className="log-item-prop">
                                            <span className="prop-key">{k}:</span> {String(v)}
                                        </div>
                                    ))
                                ) : String(item)}
                            </div>
                        ))}
                    </div>
                );
            }
            if (typeof val === 'object' && val !== null) {
                return <pre className="log-json-pretty">{JSON.stringify(val, null, 2)}</pre>;
            }
            return String(val);
        };

        return (
            <div className="log-details-grid">
                {mainKeys.map((key) => {
                    const value = details[key];
                    const oldValue = details[`_old_${key}`];
                    const hasChanged = oldValue !== undefined;

                    return (
                        <div key={key} className="log-detail-item">
                            <span className="log-detail-key">{key}:</span>
                            <div className="log-detail-value-container">
                                <div className="current-value" style={{ fontWeight: hasChanged ? 'bold' : 'normal' }}>
                                    {renderValue(value, key)}
                                </div>
                                {hasChanged && (
                                    <div className="old-value-hint">
                                        (Anterior: {renderValue(oldValue, key)})
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const handleRedundancySubmit = async (e) => {
        e.preventDefault();
        const res = await fetch('/api/admin/redundancy', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(redundancyConfig),
            credentials: 'include'
        });
        if (res.ok) {
            alert('Configurações de redundância atualizadas!');
        } else {
            alert('Erro ao atualizar configurações.');
        }
    };

    const handleSendEmail = async (e) => {
        e.preventDefault();
        setSendingEmail(true);

        const signature = "\n\n---\nEste e-mail é enviado automaticamente pelo sistema da RA Multi Services.\nPor favor, não responda a este e-mail.";
        const fullMessage = emailData.text + signature;

        try {
            const res = await fetch('/api/admin/send-email', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    to: emailData.to,
                    subject: emailData.subject,
                    text: fullMessage
                }),
                credentials: 'include'
            });
            const data = await res.json();
            if (res.ok) {
                alert('E-mail enviado com sucesso para: ' + emailData.to);
                setEmailData({ to: '', subject: '', text: '' });
            } else {
                alert('Erro ao enviar e-mail: ' + (data.message || 'Erro desconhecido.'));
            }
        } catch (error) {
            console.error(error);
            alert('Erro de comunicação com o servidor.');
        } finally {
            setSendingEmail(false);
        }
    };

    const handleCleanupLogs = async () => {
        if (logCleanupRetention === '0') return;

        const monthsLabel = logCleanupRetention === '3' ? '3 meses' : logCleanupRetention === '6' ? '6 meses' : '1 ano';
        if (!window.confirm(`Isso irá deletar PERMANENTEMENTE todos os logs mais velhos que ${monthsLabel}. Deseja continuar?`)) return;
        if (!window.confirm(`Você tem CERTEZA absoluta? Esta ação não pode ser desfeita.`)) return;

        try {
            const res = await fetch('/api/admin/logs/cleanup', {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ months: parseInt(logCleanupRetention) }),
                credentials: 'include'
            });
            const data = await res.json();
            if (res.ok) {
                alert(data.message);
                fetchLogs();
            } else {
                alert('Erro: ' + data.message);
            }
        } catch (error) {
            alert('Erro ao conectar com o servidor para limpeza.');
        }
    };

    const resetFilters = () => {
        setLogFilters({ username: '', tableName: '', recordId: '', startDate: '', endDate: '' });
    };

    const handleToggleMaintenance = async (enabled) => {
        const res = await fetch('/api/admin/redundancy', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...redundancyConfig, maintenance_mode: enabled ? '1' : '0' }),
            credentials: 'include'
        });
        if (res.ok) {
            setRedundancyConfig(prev => ({ ...prev, maintenance_mode: enabled ? '1' : '0' }));
            fetchSyncStatus();
            alert(enabled ? 'Modo de Manutenção ATIVADO. O sistema está travado para sincronia.' : 'Modo de Manutenção DESATIVADO.');
        }
    };

    const handleManualSync = async (action) => {
        const msg = action === 'push'
            ? 'Deseja forçar o ENVIO de todos os logs pendentes para o outro servidor?'
            : 'Deseja solicitar o RECEBIMENTO de novos dados do outro servidor?';

        if (!window.confirm(msg)) return;

        const endpoint = action === 'push' ? '/api/admin/sync/force-push' : '/api/admin/sync/force-pull';

        try {
            const res = await fetch(endpoint, { method: 'POST', credentials: 'include' });
            const data = await res.json();
            if (res.ok) {
                alert(data.message || 'Sincronização iniciada!');
                fetchSyncStatus();
            } else {
                alert('Erro: ' + data.message);
            }
        } catch (error) {
            alert('Falha na comunicação com o servidor.');
        }
    };

    if (loading) return <div className="loading-overlay">Carregando...</div>;

    return (
        <div className="settings-container">
            <Navbar user={user} />

            <div className="settings-window">
                <div className="win-header">Configurações do Sistema</div>

                <div className="tabs-header">
                    <button className={activeTab === 'perfil' ? 'active' : ''} onClick={() => setActiveTab('perfil')}>Meu Perfil</button>
                    <button className={activeTab === 'seguranca' ? 'active' : ''} onClick={() => setActiveTab('seguranca')}>Segurança</button>
                    {user?.isAdmin && (
                        <>
                            <button className={activeTab === 'admin' ? 'active' : ''} onClick={() => setActiveTab('admin')}>Administração</button>
                            <button className={activeTab === 'logs' ? 'active' : ''} onClick={() => setActiveTab('logs')}>Logs do Sistema</button>
                            <button className={activeTab === 'redundancia' ? 'active' : ''} onClick={() => setActiveTab('redundancia')}>Redundância/Sync</button>
                            <button className={activeTab === 'email' ? 'active' : ''} onClick={() => setActiveTab('email')}>E-mail</button>
                            <button className={activeTab === 'bloqueios' ? 'active' : ''} onClick={() => setActiveTab('bloqueios')}>Bloqueios</button>
                        </>
                    )}
                </div>

                <div className="tab-content">
                    {activeTab === 'perfil' && (
                        <div className="tab-pane">
                            <form onSubmit={handleProfileSubmit}>
                                <div className="profile-top">
                                    <div className="profile-img-box">
                                        <img
                                            src={profileData.photo
                                                ? (profileData.photo.startsWith('data:') ? profileData.photo : `/uploads/${profileData.photo}`)
                                                : '/default-avatar.png'}
                                            alt="Avatar"
                                        />
                                        <input type="file" accept="image/*" onChange={handlePhotoUpload} />
                                    </div>
                                    <div className="profile-fields">
                                        <div className="form-group">
                                            <label>Nome Completo</label>
                                            <input type="text" value={profileData.full_name} onChange={e => setProfileData({ ...profileData, full_name: e.target.value })} />
                                        </div>
                                        <div className="form-group">
                                            <label>Usuário</label>
                                            <input type="text" value={profileData.username} onChange={e => setProfileData({ ...profileData, username: e.target.value })} />
                                        </div>
                                        <div className="form-group">
                                            <label>E-mail</label>
                                            <input type="email" value={profileData.email} onChange={e => setProfileData({ ...profileData, email: e.target.value })} />
                                        </div>
                                    </div>
                                </div>
                                <button type="submit" className="btn-classic">Salvar Alterações</button>
                            </form>
                        </div>
                    )}

                    {activeTab === 'seguranca' && (
                        <div className="tab-pane">
                            <div className="security-sections">
                                <div className="sec-box">
                                    <h3>Alterar Senha</h3>
                                    <form onSubmit={handlePasswordSubmit}>
                                        <div className="form-group">
                                            <label>Senha Atual</label>
                                            <input type="password" value={passwordData.currentPassword} onChange={e => setPasswordData({ ...passwordData, currentPassword: e.target.value })} required />
                                        </div>
                                        <div className="form-group">
                                            <label>Nova Senha</label>
                                            <input type="password" value={passwordData.newPassword} onChange={e => setPasswordData({ ...passwordData, newPassword: e.target.value })} required />
                                        </div>
                                        <div className="form-group">
                                            <label>Confirmar Nova Senha</label>
                                            <input type="password" value={passwordData.confirmPassword} onChange={e => setPasswordData({ ...passwordData, confirmPassword: e.target.value })} required />
                                        </div>
                                        {user?.two_factor_enabled && (
                                            <div className="form-group">
                                                <label>Código 2FA</label>
                                                <input type="text" value={passwordData.mfaToken} onChange={e => setPasswordData({ ...passwordData, mfaToken: e.target.value })} required />
                                            </div>
                                        )}
                                        <button type="submit" className="btn-classic">Atualizar Senha</button>
                                    </form>
                                </div>

                                <div className="sec-box">
                                    <h3>Autenticação de Dois Fatores (2FA)</h3>
                                    <p>Status: <strong>{user?.two_factor_enabled ? 'ATIVADO' : 'DESATIVADO'}</strong></p>
                                    {user?.two_factor_enabled && (
                                        <p>Método: <strong>{user?.two_factor_method === 'app' ? 'Aplicativo' : 'E-mail'}</strong></p>
                                    )}

                                    {!user?.two_factor_enabled ? (
                                        <div className="mfa-actions">
                                            <button className="btn-classic" onClick={() => handleMFASetup('enable', 'email')}>Ativar via E-mail</button>
                                            <button className="btn-classic" onClick={() => handleMFASetup('generate_qr', 'app')}>Ativar via Aplicativo (Google/App)</button>
                                        </div>
                                    ) : (
                                        <div className="mfa-actions">
                                            {!mfaSetup.isConfirmingDisable ? (
                                                <button className="btn-classic danger" onClick={() => handleMFASetup('disable')}>Desativar 2FA</button>
                                            ) : (
                                                <div className="mfa-confirm-box" style={{ marginTop: '10px', padding: '10px', background: '#f8f8f8', border: '1px dashed #808080' }}>
                                                    <p style={{ fontSize: '0.85rem', marginBottom: '10px' }}>Insira o código enviado para seu e-mail para confirmar a desativação:</p>
                                                    <input
                                                        type="text"
                                                        placeholder="Código de 6 dígitos"
                                                        value={mfaSetup.token}
                                                        onChange={e => setMfaSetup({ ...mfaSetup, token: e.target.value })}
                                                        style={{ width: '100%', marginBottom: '10px', textAlign: 'center', letterSpacing: '2px', fontWeight: 'bold' }}
                                                    />
                                                    <div style={{ display: 'flex', gap: '5px' }}>
                                                        <button className="btn-classic primary" style={{ flex: 1 }} onClick={() => handleMFASetup('confirm_disable', null, mfaSetup.token)}>Confirmar Desativação</button>
                                                        <button className="btn-classic" onClick={() => setMfaSetup({ ...mfaSetup, isConfirmingDisable: false, token: '' })}>Cancelar</button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {mfaSetup.showQR && (
                                        <div className="mfa-qr-box win-style">
                                            <p>Escaneie o código abaixo ou insira a chave:</p>
                                            <div className="mfa-secret">{mfaSetup.secret}</div>
                                            <input type="text" placeholder="Insira o código gerado no app" value={mfaSetup.token} onChange={e => setMfaSetup({ ...mfaSetup, token: e.target.value })} />
                                            <button className="btn-classic" onClick={() => handleMFASetup('enable', 'app', mfaSetup.token)}>Validar e Ativar</button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'admin' && (
                        <div className="tab-pane">
                            <div className="admin-header">
                                <h3>Gerenciamento de Usuários</h3>
                                <button className="btn-classic" onClick={() => setShowAdminModal(true)}>+ Novo Usuário</button>
                            </div>

                            <table className="admin-table">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Usuário</th>
                                        <th>E-mail</th>
                                        <th>Admin</th>
                                        <th>2FA</th>
                                        <th>Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {adminUsers.map(u => (
                                        <tr key={u.id}>
                                            <td>{u.id}</td>
                                            <td>{u.username}</td>
                                            <td>{u.email}</td>
                                            <td>{u.isAdmin ? 'Sim' : 'Não'}</td>
                                            <td>{u.two_factor_enabled ? (u.two_factor_method === 'app' ? 'App' : 'Email') : 'OFF'}</td>
                                            <td className="admin-actions">
                                                {u.username !== 'flp' && <button onClick={() => handleEditUser(u)}>Editar</button>}
                                                {u.username !== 'flp' && <button onClick={() => handleAdminReset(u.id)}>Reset Senha</button>}
                                                {u.two_factor_enabled && u.username !== 'flp' && (
                                                    <button onClick={() => handleAdminRemove2FA(u.id)}>Remover 2FA</button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {showAdminModal && (
                                <div className="admin-modal-overlay">
                                    <div className="admin-modal win-style">
                                        <div className="win-header">Criar Novo Usuário</div>
                                        <form onSubmit={handleAdminCreate} className="win-body">
                                            <div className="form-group">
                                                <label>Usuário</label>
                                                <input type="text" value={newAdminUser.username} onChange={e => setNewAdminUser({ ...newAdminUser, username: e.target.value })} required />
                                            </div>
                                            <div className="form-group">
                                                <label>Senha Inicial</label>
                                                <input type="password" value={newAdminUser.password} onChange={e => setNewAdminUser({ ...newAdminUser, password: e.target.value })} required />
                                            </div>
                                            <div className="form-group">
                                                <label>E-mail</label>
                                                <input type="email" value={newAdminUser.email} onChange={e => setNewAdminUser({ ...newAdminUser, email: e.target.value })} required />
                                            </div>
                                            <div className="form-group">
                                                <label>Nome Completo</label>
                                                <input type="text" value={newAdminUser.full_name} onChange={e => setNewAdminUser({ ...newAdminUser, full_name: e.target.value })} />
                                            </div>
                                            <div className="form-group checkbox">
                                                <label>
                                                    <input type="checkbox" checked={newAdminUser.isAdmin} onChange={e => setNewAdminUser({ ...newAdminUser, isAdmin: e.target.checked })} />
                                                    É Admin?
                                                </label>
                                            </div>

                                            {!newAdminUser.isAdmin && (
                                                <>
                                                    <div className="form-group" style={{ marginTop: '10px' }}>
                                                        <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Abas Permitidas:</label>
                                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px' }}>
                                                            {['clientes', 'produtos', 'funcionarios', 'financeiro', 'fornecedores', 'calendario', 'cotacoes'].map(tab => (
                                                                <label key={tab} style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={newAdminUser.allowedTabs.includes(tab)}
                                                                        onChange={e => {
                                                                            const newTabs = e.target.checked
                                                                                ? [...newAdminUser.allowedTabs, tab]
                                                                                : newAdminUser.allowedTabs.filter(t => t !== tab);
                                                                            setNewAdminUser({ ...newAdminUser, allowedTabs: newTabs });
                                                                        }}
                                                                    />
                                                                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                                                                </label>
                                                            ))}
                                                        </div>
                                                    </div>

                                                    <div className="form-group" style={{ marginTop: '15px' }}>
                                                        <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Widgets Permitidos:</label>
                                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px' }}>
                                                            {[
                                                                { id: 'weather', label: 'Clima' },
                                                                { id: 'calendar', label: 'Calendário' },
                                                                { id: 'finance', label: 'Financeiro' },
                                                                { id: 'notes', label: 'Notas' },
                                                                { id: 'quotations', label: 'Cotações' }
                                                            ].map(w => (
                                                                <label key={w.id} style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={newAdminUser.allowedWidgets.includes(w.id)}
                                                                        onChange={e => {
                                                                            const newWidgets = e.target.checked
                                                                                ? [...newAdminUser.allowedWidgets, w.id]
                                                                                : newAdminUser.allowedWidgets.filter(x => x !== w.id);
                                                                            setNewAdminUser({ ...newAdminUser, allowedWidgets: newWidgets });
                                                                        }}
                                                                    />
                                                                    {w.label}
                                                                </label>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                            <div className="modal-actions">
                                                <button type="submit" className="btn-classic">Criar</button>
                                                <button type="button" className="btn-classic" onClick={() => setShowAdminModal(false)}>Cancelar</button>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            )}

                            {showEditModal && editingUser && (
                                <div className="admin-modal-overlay">
                                    <div className="admin-modal win-style">
                                        <div className="win-header">Editar Usuário: {editingUser.username}</div>
                                        <form onSubmit={handleAdminUpdateUser} className="win-body">
                                            <div className="form-group">
                                                <label>E-mail</label>
                                                <input type="email" value={editingUser.email} onChange={e => setEditingUser({ ...editingUser, email: e.target.value })} required />
                                            </div>
                                            <div className="form-group checkbox">
                                                <label>
                                                    <input type="checkbox" checked={editingUser.newIsAdmin} onChange={e => setEditingUser({ ...editingUser, newIsAdmin: e.target.checked })} />
                                                    É Admin?
                                                </label>
                                            </div>

                                            {!editingUser.newIsAdmin && (
                                                <>
                                                    <div className="form-group" style={{ marginTop: '10px' }}>
                                                        <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Abas Permitidas:</label>
                                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px' }}>
                                                            {['clientes', 'produtos', 'funcionarios', 'financeiro', 'fornecedores', 'calendario', 'cotacoes'].map(tab => (
                                                                <label key={tab} style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={editingUser.allowedTabs.includes(tab)}
                                                                        onChange={e => {
                                                                            const newTabs = e.target.checked
                                                                                ? [...editingUser.allowedTabs, tab]
                                                                                : editingUser.allowedTabs.filter(t => t !== tab);
                                                                            setEditingUser({ ...editingUser, allowedTabs: newTabs });
                                                                        }}
                                                                    />
                                                                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                                                                </label>
                                                            ))}
                                                        </div>
                                                    </div>

                                                    <div className="form-group" style={{ marginTop: '15px' }}>
                                                        <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Widgets Permitidos:</label>
                                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px' }}>
                                                            {[
                                                                { id: 'weather', label: 'Clima' },
                                                                { id: 'calendar', label: 'Calendário' },
                                                                { id: 'finance', label: 'Financeiro' },
                                                                { id: 'notes', label: 'Notas' },
                                                                { id: 'quotations', label: 'Cotações' }
                                                            ].map(w => (
                                                                <label key={w.id} style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={editingUser.allowedWidgets.includes(w.id)}
                                                                        onChange={e => {
                                                                            const newWidgets = e.target.checked
                                                                                ? [...editingUser.allowedWidgets, w.id]
                                                                                : editingUser.allowedWidgets.filter(x => x !== w.id);
                                                                            setEditingUser({ ...editingUser, allowedWidgets: newWidgets });
                                                                        }}
                                                                    />
                                                                    {w.label}
                                                                </label>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </>
                                            )}

                                            <div className="modal-actions">
                                                <button type="submit" className="btn-classic">Salvar</button>
                                                <button type="button" className="btn-classic" onClick={() => setShowEditModal(false)}>Cancelar</button>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'logs' && (
                        <div className="tab-pane">
                            <div className="admin-header">
                                <h3>Logs de Atividade</h3>
                                <button className="btn-classic" onClick={fetchLogs}>Aplicar Filtros / Atualizar</button>
                            </div>

                            <div className="log-filters-bar win-style" style={{ marginBottom: '15px', padding: '10px', display: 'flex', gap: '10px', flexWrap: 'wrap', backgroundColor: '#f0f0f0', border: '1px solid #ccc' }}>
                                <div className="filter-item">
                                    <label style={{ fontSize: '0.8rem', display: 'block' }}>Usuário</label>
                                    <input type="text" className="win-input" value={logFilters.username} onChange={e => setLogFilters({ ...logFilters, username: e.target.value })} placeholder="Ex: admin" style={{ width: '100px' }} />
                                </div>
                                <div className="filter-item">
                                    <label style={{ fontSize: '0.8rem', display: 'block' }}>Tabela</label>
                                    <select className="win-input" value={logFilters.tableName} onChange={e => setLogFilters({ ...logFilters, tableName: e.target.value })}>
                                        <option value="">Todas</option>
                                        <option value="Clientes">Clientes</option>
                                        <option value="Produtos">Produtos</option>
                                        <option value="Fornecedores">Fornecedores</option>
                                        <option value="Cotacoes">Cotações</option>
                                        <option value="LancamentosFinanceiros">Financeiro</option>
                                        <option value="calendar_events">Calendário</option>
                                        <option value="users">Usuários</option>
                                    </select>
                                </div>
                                <div className="filter-item">
                                    <label style={{ fontSize: '0.8rem', display: 'block' }}>ID Registro</label>
                                    <input type="text" className="win-input" value={logFilters.recordId} onChange={e => setLogFilters({ ...logFilters, recordId: e.target.value })} placeholder="Ex: 123" style={{ width: '80px' }} />
                                </div>
                                <div className="filter-item">
                                    <label style={{ fontSize: '0.8rem', display: 'block' }}>Data Início (DD/MM/AAAA)</label>
                                    <input
                                        type="date"
                                        className="win-input"
                                        lang="pt-BR"
                                        value={logFilters.startDate}
                                        onChange={e => setLogFilters({ ...logFilters, startDate: e.target.value })}
                                    />
                                </div>
                                <div className="filter-item">
                                    <label style={{ fontSize: '0.8rem', display: 'block' }}>Data Fim (DD/MM/AAAA)</label>
                                    <input
                                        type="date"
                                        className="win-input"
                                        lang="pt-BR"
                                        value={logFilters.endDate}
                                        onChange={e => setLogFilters({ ...logFilters, endDate: e.target.value })}
                                    />
                                </div>
                                <div className="filter-item" style={{ display: 'flex', alignItems: 'flex-end' }}>
                                    <button className="btn-classic btn-sm" onClick={resetFilters}>Limpar</button>
                                </div>
                            </div>

                            <div className="logs-table-wrapper">
                                <table className="admin-table logs-table">
                                    <thead>
                                        <tr>
                                            <th>Data</th>
                                            <th>Usuário</th>
                                            <th>Ação</th>
                                            <th>Tabela</th>
                                            <th>ID</th>
                                            <th>Detalhes</th>
                                            <th>IP / Dispositivo</th>
                                            <th>Sync</th>
                                            <th>Ação</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {systemLogs.map(log => (
                                            <tr key={log.id}>
                                                <td style={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                                                    {new Date(log.timestamp).toLocaleDateString('pt-BR')} {new Date(log.timestamp).toLocaleTimeString('pt-BR')}
                                                </td>
                                                <td>{log.username || 'Sistema'}</td>
                                                <td>{log.action}</td>
                                                <td>{log.table_name}</td>
                                                <td>{log.record_id}</td>
                                                <td style={{ textAlign: 'center' }}>
                                                    {log.action === 'INSERT' ? '➕' : log.action === 'UPDATE' ? '📝' : log.action === 'DELETE' ? '🗑️' : 'ℹ️'}
                                                </td>
                                                <td style={{ fontSize: '0.7rem', color: '#666' }}>
                                                    {log.ip_address || 'N/A'}<br />
                                                    <span style={{ fontSize: '0.65rem' }}>{log.os || ''} {log.browser || ''}</span>
                                                </td>
                                                <td>{log.synced ? '✅' : '⏳'}</td>
                                                <td>
                                                    <button className="btn-classic btn-sm" onClick={() => handleViewLogDetails(log)}>
                                                        Ver Detalhes
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {activeTab === 'redundancia' && (
                        <div className="tab-pane">
                            <h3>Configuração de Host + Local</h3>
                            <div className="sec-box">
                                <form onSubmit={handleRedundancySubmit}>
                                    <div className="form-group">
                                        <label>Modo deste Servidor</label>
                                        <select
                                            value={redundancyConfig.node_mode}
                                            onChange={e => setRedundancyConfig({ ...redundancyConfig, node_mode: e.target.value })}
                                            className="win-input"
                                        >
                                            <option value="host">Principal (Host Online)</option>
                                            <option value="local">Empresa (Servidor Local)</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>URL do Outro Servidor (Peer URL)</label>
                                        <input
                                            type="text"
                                            placeholder="Ex: http://ra-local.com:3003"
                                            value={redundancyConfig.peer_url}
                                            onChange={e => setRedundancyConfig({ ...redundancyConfig, peer_url: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Segredo de Sincronização (Sync Secret)</label>
                                        <input
                                            type="password"
                                            value={redundancyConfig.sync_secret}
                                            onChange={e => setRedundancyConfig({ ...redundancyConfig, sync_secret: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Frequência de Sincronização</label>
                                        <select
                                            value={redundancyConfig.sync_frequency}
                                            onChange={e => setRedundancyConfig({ ...redundancyConfig, sync_frequency: e.target.value })}
                                            className="win-input"
                                        >
                                            <option value="instant">A cada momento (Imediato)</option>
                                            <option value="1m">A cada 1 minuto</option>
                                            <option value="5m">A cada 5 minutos</option>
                                            <option value="1h">A cada 1 hora</option>
                                            <option value="1d">Diariamente (Agendado)</option>
                                            <option value="1w">Semanalmente (Agendado)</option>
                                        </select>
                                    </div>

                                    {(redundancyConfig.sync_frequency === '1d' || redundancyConfig.sync_frequency === '1w') && (
                                        <div className="form-group">
                                            <label>Horário da Sincronização</label>
                                            <input
                                                type="time"
                                                className="win-input"
                                                value={redundancyConfig.sync_schedule_time}
                                                onChange={e => setRedundancyConfig({ ...redundancyConfig, sync_schedule_time: e.target.value })}
                                            />
                                        </div>
                                    )}

                                    {redundancyConfig.sync_frequency === '1w' && (
                                        <div className="form-group">
                                            <label>Dia da Semana</label>
                                            <select
                                                className="win-input"
                                                value={redundancyConfig.sync_schedule_day}
                                                onChange={e => setRedundancyConfig({ ...redundancyConfig, sync_schedule_day: e.target.value })}
                                            >
                                                <option value="0">Domingo</option>
                                                <option value="1">Segunda-feira</option>
                                                <option value="2">Terça-feira</option>
                                                <option value="3">Quarta-feira</option>
                                                <option value="4">Quinta-feira</option>
                                                <option value="5">Sexta-feira</option>
                                                <option value="6">Sábado</option>
                                            </select>
                                        </div>
                                    )}

                                    <div className="form-group checkbox">
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={redundancyConfig.sync_enabled === '1'}
                                                onChange={e => setRedundancyConfig({ ...redundancyConfig, sync_enabled: e.target.checked ? '1' : '0' })}
                                            />
                                            Ativar Sincronização Automática
                                        </label>
                                    </div>
                                    <button type="submit" className="btn-classic">Salvar Configurações</button>
                                </form>


                                <hr style={{ margin: '20px 0', border: '0', borderTop: '1px solid #ccc' }} />

                                <div className="manual-actions">
                                    <h4>Ações de Sincronia Manuais</h4>

                                    <div className={`sync-status-box ${!syncStatus.enabled ? 'neutral' : !syncStatus.isConfigured ? 'error' : syncStatus.pendingCount > 0 || syncStatus.hasInconsistency ? 'warning' : 'success'}`} style={{ padding: '15px', borderRadius: '8px', marginBottom: '15px', border: '1px solid #ccc', background: !syncStatus.enabled ? '#f0f0f0' : !syncStatus.isConfigured ? '#fff2f0' : (syncStatus.pendingCount > 0 || syncStatus.hasInconsistency ? '#fffbe6' : '#f6ffed') }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div>
                                                <strong>Status:</strong> {
                                                    !syncStatus.enabled ? '⚪ Desativado' :
                                                        !syncStatus.isConfigured ? '❌ Redundância Não Configurada' :
                                                            syncStatus.pendingCount > 0 ? '⏳ Sincronia Pendente' :
                                                                syncStatus.hasInconsistency ? '⚠️ Incoerência Detectada' :
                                                                    '✅ Servidores Sincronizados'
                                                }
                                                <br />
                                                <small>{syncStatus.enabled ? `${syncStatus.pendingCount} logs pendentes` : 'Habilite a sincronização automática acima.'}</small>
                                            </div>
                                            <button type="button" className="btn-classic" onClick={fetchSyncStatus}>🔄 Atualizar Status</button>
                                        </div>
                                    </div>

                                    <div className="maintenance-toggle-box" style={{ marginBottom: '20px', padding: '15px', background: redundancyConfig.maintenance_mode === '1' ? '#fff1f0' : '#f6ffed', border: '1px solid #ccc', borderRadius: '8px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div>
                                                <strong>🔒 Modo de Manutenção (Trava do Sistema)</strong>
                                                <p style={{ fontSize: '0.8rem', margin: '5px 0 0 0' }}>Ative para travar escritas no sistema durante sincronias pesadas.</p>
                                            </div>
                                            <div className="win-switch">
                                                <input
                                                    type="checkbox"
                                                    id="maintenance-toggle"
                                                    checked={redundancyConfig.maintenance_mode === '1'}
                                                    onChange={(e) => handleToggleMaintenance(e.target.checked)}
                                                />
                                                <label htmlFor="maintenance-toggle"></label>
                                            </div>
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', gap: '10px' }}>
                                        <button type="button" className="btn-classic" onClick={() => handleManualSync('push')}>
                                            Forçar Envio (Upload)
                                        </button>
                                        <button type="button" className="btn-classic" onClick={() => handleManualSync('pull')}>
                                            Forçar Recebimento (Download)
                                        </button>
                                    </div>
                                </div>

                                <div style={{ marginTop: '20px', padding: '10px', backgroundColor: '#e6f7ff', border: '1px solid #91d5ff', fontSize: '0.8rem' }}>
                                    <strong>Nota:</strong> O sistema híbrido usa logs para rapidez e comparação de banco de dados para integridade total.
                                </div>
                            </div>

                            <div className="sec-box" style={{ marginTop: '20px' }}>
                                <h3>Manutenção de Logs</h3>
                                <p style={{ fontSize: '0.9rem', color: '#666' }}>Remover logs antigos para economizar espaço e melhorar a performance.</p>
                                <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '15px', marginTop: '10px' }}>
                                    <label>Limpar logs mais velhos que:</label>
                                    <select
                                        className="win-input"
                                        value={logCleanupRetention}
                                        onChange={e => setLogCleanupRetention(e.target.value)}
                                        style={{ width: '150px' }}
                                    >
                                        <option value="0">Não Limpar</option>
                                        <option value="3">3 meses</option>
                                        <option value="6">6 meses</option>
                                        <option value="12">1 ano (anual)</option>
                                    </select>
                                    <button
                                        className="btn-classic danger"
                                        onClick={handleCleanupLogs}
                                        disabled={logCleanupRetention === '0'}
                                    >
                                        Realizar Limpeza Agora
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'email' && user?.isAdmin && (
                        <div className="tab-pane">
                            <div className="email-tab-container">
                                <div className="sec-box">
                                    <h3>Enviar E-mail Manual</h3>
                                    <form onSubmit={handleSendEmail}>
                                        <div className="form-group">
                                            <label>Para:</label>
                                            <input type="email" value={emailData.to} onChange={e => setEmailData({ ...emailData, to: e.target.value })} required />
                                        </div>
                                        <div className="form-group">
                                            <label>Assunto:</label>
                                            <input type="text" value={emailData.subject} onChange={e => setEmailData({ ...emailData, subject: e.target.value })} required />
                                        </div>
                                        <div className="form-group">
                                            <label>Mensagem:</label>
                                            <textarea value={emailData.text} onChange={e => setEmailData({ ...emailData, text: e.target.value })} required rows="6"></textarea>
                                        </div>
                                        <button type="submit" className="btn-classic" disabled={sendingEmail}>
                                            {sendingEmail ? 'Enviando...' : 'Enviar E-mail'}
                                        </button>
                                    </form>
                                </div>

                                <div className="sec-box" style={{ marginTop: '20px' }}>
                                    <h3>Gerenciamento de Notificações</h3>
                                    <p style={{ marginBottom: '15px', color: '#666', fontSize: '0.9rem' }}>
                                        Configure quem deve receber e-mails automáticos para cada tipo de alerta.
                                    </p>
                                    <div className="notifications-management-table">
                                        <table className="admin-table">
                                            <thead>
                                                <tr>
                                                    <th>Usuário</th>
                                                    <th>E-mail</th>
                                                    <th style={{ textAlign: 'center' }}>💰 Financeiro</th>
                                                    <th style={{ textAlign: 'center' }}>📢 Cotações</th>
                                                    <th style={{ textAlign: 'center' }}>⚠️ Segurança</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {notificationSettings.map(u => (
                                                    <tr key={u.id}>
                                                        <td>{u.full_name || u.username}</td>
                                                        <td>{u.email}</td>
                                                        <td style={{ textAlign: 'center' }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={u.alerts.includes('financeiro')}
                                                                onChange={(e) => handleToggleNotification(u.id, 'financeiro', e.target.checked)}
                                                            />
                                                        </td>
                                                        <td style={{ textAlign: 'center' }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={u.alerts.includes('cotacoes')}
                                                                onChange={(e) => handleToggleNotification(u.id, 'cotacoes', e.target.checked)}
                                                            />
                                                        </td>
                                                        <td style={{ textAlign: 'center' }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={u.alerts.includes('seguranca')}
                                                                onChange={(e) => handleToggleNotification(u.id, 'seguranca', e.target.checked)}
                                                            />
                                                        </td>
                                                    </tr>
                                                ))}
                                                {notificationSettings.length === 0 && (
                                                    <tr>
                                                        <td colSpan="5" style={{ textAlign: 'center', padding: '20px' }}>Carregando usuários...</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
                                        <button
                                            onClick={handleSaveNotifications}
                                            className="btn-classic primary"
                                            disabled={savingNotifications}
                                            style={{ padding: '8px 25px' }}
                                        >
                                            {savingNotifications ? 'Salvando...' : 'Salvar Destinatários'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'bloqueios' && user?.isAdmin && (
                        <div className="tab-pane">
                            <div className="admin-header">
                                <h3>Gerenciamento de Defesa Cibernética</h3>
                                <div className="admin-actions">
                                    <button className="btn-classic" onClick={() => {
                                        fetchBlockedIPs();
                                        fetchTrustedIPs();
                                    }} disabled={loadingBlocks || loadingTrusted}>
                                        {loadingBlocks || loadingTrusted ? 'Atualizando...' : '🔄 Atualizar Tudo'}
                                    </button>
                                </div>
                            </div>

                            <div className="security-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: '20px', marginTop: '15px' }}>
                                {/* Coluna Esquerda: Formulário de Bloqueio Manual */}
                                <div className="sec-box win-style" style={{ padding: '15px', background: '#f8f8f8', border: '1px solid #ccc' }}>
                                    <h4 style={{ marginBottom: '10px' }}>🛡️ Bloqueio Manual</h4>
                                    <form onSubmit={handleManualBlock}>
                                        <div className="form-group">
                                            <label>Endereço IP:</label>
                                            <input
                                                type="text"
                                                className="win-input"
                                                value={newBlock.ip_address}
                                                onChange={e => setNewBlock({ ...newBlock, ip_address: e.target.value })}
                                                placeholder="ex: 123.123.123.123"
                                                required
                                            />
                                        </div>
                                        <div className="form-group" style={{ marginTop: '10px' }}>
                                            <label>Motivo:</label>
                                            <input
                                                type="text"
                                                className="win-input"
                                                value={newBlock.reason}
                                                onChange={e => setNewBlock({ ...newBlock, reason: e.target.value })}
                                                placeholder="ex: Tentativa suspeita"
                                                required
                                            />
                                        </div>
                                        <div className="form-group" style={{ marginTop: '10px' }}>
                                            <label>Expira em (opcional):</label>
                                            <input
                                                type="datetime-local"
                                                className="win-input"
                                                value={newBlock.expires_at}
                                                onChange={e => setNewBlock({ ...newBlock, expires_at: e.target.value })}
                                            />
                                            <small style={{ color: '#888' }}>Deixe em branco para bloqueio permanente.</small>
                                        </div>
                                        <button type="submit" className="btn-classic primary" style={{ marginTop: '15px', width: '100%' }}>
                                            Bloquear IP Agora
                                        </button>
                                    </form>
                                </div>

                                {/* Coluna Direita: Lista de IPs Bloqueados */}
                                <div className="sec-box">
                                    <h4 style={{ marginBottom: '10px' }}>🚫 IPs Bloqueados</h4>
                                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                        <table className="admin-table">
                                            <thead>
                                                <tr>
                                                    <th>IP</th>
                                                    <th>Motivo</th>
                                                    <th>Expira em</th>
                                                    <th>Ação</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {blockedIPs.length > 0 ? blockedIPs.map(block => (
                                                    <tr key={block.id}>
                                                        <td>{block.ip_address}</td>
                                                        <td title={block.reason}>{block.reason.length > 20 ? block.reason.substring(0, 20) + '...' : block.reason}</td>
                                                        <td>{block.expires_at ? new Date(block.expires_at).toLocaleString('pt-BR') : 'Permanente'}</td>
                                                        <td>
                                                            <button className="btn-classic btn-sm danger" onClick={() => handleUnblockIP(block.id)}>Desbloquear</button>
                                                        </td>
                                                    </tr>
                                                )) : (
                                                    <tr><td colSpan="4" style={{ textAlign: 'center', padding: '10px' }}>Nenhum bloqueio ativo.</td></tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>

                            <hr style={{ margin: '30px 0', border: '0', borderTop: '1px solid #ccc' }} />

                            {/* Seção: IPs Confiáveis (Whitelist) */}
                            <div className="sec-box">
                                <div className="admin-header" style={{ marginBottom: '10px' }}>
                                    <h3>✅ IPs Confiáveis (Whitelist)</h3>
                                    <button className="btn-classic primary" onClick={() => {
                                        setIsEditingTrusted(false);
                                        setTrustedFormData({ id: '', ip_address: '', description: '' });
                                        setShowTrustedModal(true);
                                    }}>
                                        + Adicionar IP Confiável
                                    </button>
                                </div>
                                <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '15px' }}>
                                    IPs nesta lista ignoram as verificações de honeypot e nunca são bloqueados automaticamente pelo sistema.
                                </p>

                                <table className="admin-table">
                                    <thead>
                                        <tr>
                                            <th>Endereço IP</th>
                                            <th>Descrição / Nome</th>
                                            <th>Adicionado em</th>
                                            <th>Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {trustedIPs.length > 0 ? trustedIPs.map(tip => (
                                            <tr key={tip.id}>
                                                <td><strong>{tip.ip_address}</strong></td>
                                                <td>{tip.description}</td>
                                                <td>{new Date(tip.added_at).toLocaleString('pt-BR')}</td>
                                                <td>
                                                    <button className="btn-classic btn-sm" onClick={() => {
                                                        setIsEditingTrusted(true);
                                                        setTrustedFormData({ id: tip.id, ip_address: tip.ip_address, description: tip.description });
                                                        setShowTrustedModal(true);
                                                    }}>Editar</button>
                                                    <button className="btn-classic btn-sm danger" style={{ marginLeft: '5px' }} onClick={() => handleDeleteTrusted(tip.id)}>Excluir</button>
                                                </td>
                                            </tr>
                                        )) : (
                                            <tr><td colSpan="4" style={{ textAlign: 'center', padding: '20px' }}>Nenhum IP confiável cadastrado.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Modal para IPs Confiáveis */}
                            {showTrustedModal && (
                                <div className="admin-modal-overlay">
                                    <div className="admin-modal win-style" style={{ maxWidth: '400px' }}>
                                        <div className="win-header">{isEditingTrusted ? 'Editar IP Confiável' : 'Adicionar IP Confiável'}</div>
                                        <div className="win-body">
                                            <form onSubmit={handleTrustedSubmit}>
                                                <div className="form-group">
                                                    <label>Endereço IP:</label>
                                                    <input
                                                        type="text"
                                                        className="win-input"
                                                        value={trustedFormData.ip_address}
                                                        onChange={e => setTrustedFormData({ ...trustedFormData, ip_address: e.target.value })}
                                                        placeholder="ex: 192.168.1.100"
                                                        required
                                                    />
                                                </div>
                                                <div className="form-group" style={{ marginTop: '10px' }}>
                                                    <label>Descrição:</label>
                                                    <input
                                                        type="text"
                                                        className="win-input"
                                                        value={trustedFormData.description}
                                                        onChange={e => setTrustedFormData({ ...trustedFormData, description: e.target.value })}
                                                        placeholder="ex: Escritório Central"
                                                        required
                                                    />
                                                </div>
                                                <div className="modal-actions" style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                                                    <button type="button" className="btn-classic" onClick={() => setShowTrustedModal(false)}>Cancelar</button>
                                                    <button type="submit" className="btn-classic primary">{isEditingTrusted ? 'Salvar Alterações' : 'Adicionar'}</button>
                                                </div>
                                            </form>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {showLogModal && selectedLog && (
                <div className="admin-modal-overlay">
                    <div className="admin-modal log-detail-modal win-style">
                        <div className="win-header">
                            Logs: {selectedLog.action} em {selectedLog.table_name} (ID: {selectedLog.record_id})
                        </div>
                        <div className="win-body">
                            <div className="log-meta-info" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '15px', padding: '10px', background: '#f9f9f9', border: '1px solid #ddd' }}>
                                <p><strong>Data:</strong> {new Date(selectedLog.timestamp).toLocaleString()}</p>
                                <p><strong>Usuário:</strong> {selectedLog.username || 'Sistema'}</p>
                                <p><strong>IP:</strong> {selectedLog.ip_address || 'N/A'}</p>
                                <p><strong>Origem:</strong> {selectedLog.node_origin}</p>
                                <p><strong>Navegador:</strong> {selectedLog.browser || 'N/A'}</p>
                                <p><strong>Sistema:</strong> {selectedLog.os || 'N/A'}</p>
                                <p><strong>Dispositivo:</strong> {selectedLog.device || 'N/A'}</p>
                            </div>
                            <div className="log-details-content">
                                <h4>Dados Alterados:</h4>
                                {renderLogDetails(selectedLog.parsedDetails)}
                            </div>
                            <div className="modal-actions" style={{ marginTop: '20px' }}>
                                <button className="btn-classic" onClick={() => setShowLogModal(false)}>Fechar</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Settings;
